import { type FC, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from './ui/field';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { Card, CardContent } from './ui/card';
import { X } from 'lucide-react';
import { useAnalysis } from '../hooks/useAnalysis';
import { useDimensions, useMeasures, useCalendarNode, fetchDimensionValues } from '../services/reportBuilder.service';
import { useReportingTree } from '../hooks/useReportingTree';
import { ReportBuilderState, SimpleGraph } from '../utils/reportBuilderState';
import type { Analysis } from '../services/analysis.service';
import type { ReportBuilderDimension, ReportBuilderMeasure } from '../services/reportBuilder.service';
import { FilterTable, type FilterEntry } from './FilterTable';
import { defaultComparatorOptions, type AnalysisDimensionFilter } from '../utils/filterComparators';

interface EditChartModalProps {
  chartId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyChanges?: (analysis: Partial<Analysis>) => void;
  defaultTab?: 'basic' | 'metrics' | 'filters';
}

const CHART_TYPES = [
  {
    value: 'SINGLE_VALUE',
    label: 'Single Value',
    description: 'Display a single metric value',
  },
  {
    value: 'TABLE',
    label: 'Table',
    description: 'Display data in a table format',
  },
  {
    value: 'BAR',
    label: 'Bar Chart',
    description: 'Visualize data with bars',
  },
  {
    value: 'LINE',
    label: 'Line Chart',
    description: 'Visualize trends over time',
  },
  {
    value: 'PIE',
    label: 'Pie Chart',
    description: 'Visualize data distribution',
  },
];

export const EditChartModal: FC<EditChartModalProps> = ({
  chartId,
  open,
  onOpenChange,
  onApplyChanges,
  defaultTab = 'basic',
}) => {
  const { data: analysis } = useAnalysis(chartId);
  const { data: dimensions } = useDimensions();
  const { data: measures } = useMeasures();
  const { data: reportingTree } = useReportingTree();
  const { data: calendarNode } = useCalendarNode();

  const [selectedChartType, setSelectedChartType] = useState<string>('TABLE');
  const [selectedMeasureIds, setSelectedMeasureIds] = useState<string[]>([]);
  const [selectedDimensionIds, setSelectedDimensionIds] = useState<string[]>([]);
  const [selectedMeasureValue, setSelectedMeasureValue] = useState<string>('');
  const [selectedDimensionValue, setSelectedDimensionValue] = useState<string>('');
  const [dimensionFilters, setDimensionFilters] = useState<AnalysisDimensionFilter[]>([]);
  const [reportBuilderState, setReportBuilderState] = useState<ReportBuilderState | null>(null);

  // Build graph from reporting tree
  const graph = useMemo(() => {
    const g = new SimpleGraph();
    if (!reportingTree) {
      return g;
    }

    reportingTree.nodes.forEach(n => {
      g.setNode(n.id, n.title);
    });

    reportingTree.edges.forEach(e => {
      g.setEdge(e.from_id, e.to_id, e.relationship_id);
    });

    return g;
  }, [reportingTree]);

  // Initialize state from analysis when it loads
  useEffect(() => {
    if (analysis) {
      setSelectedChartType(analysis.visualization_type || 'TABLE');
      setSelectedMeasureIds(analysis.measure_ids || []);
      setSelectedDimensionIds(analysis.dimension_ids || []);
      setDimensionFilters(analysis.dimension_filters || []);
    }
  }, [analysis]);

  // Initialize ReportBuilderState when data is available
  useEffect(() => {
    if (graph && dimensions && measures) {
      const state = new ReportBuilderState(
        graph,
        dimensions,
        measures,
        selectedDimensionIds,
        selectedMeasureIds
      );
      setReportBuilderState(state);
    }
  }, [graph, dimensions, measures, selectedDimensionIds, selectedMeasureIds]);

  const handleApplyChanges = useCallback(() => {
    if (!analysis) return;

    const updatedAnalysis: Partial<Analysis> = {
      ...analysis,
      visualization_type: selectedChartType,
      measure_ids: selectedMeasureIds,
      dimension_ids: selectedDimensionIds,
      dimension_filters: dimensionFilters,
    };

    // Close the modal
    onOpenChange(false);

    // Apply changes to working analysis
    if (onApplyChanges) {
      onApplyChanges(updatedAnalysis);
    }
  }, [analysis, selectedChartType, selectedMeasureIds, selectedDimensionIds, dimensionFilters, onApplyChanges, onOpenChange]);

  // Compute available and disabled options based on report builder state
  const availableMeasureIds = useMemo(() => {
    if (!reportBuilderState) return [];
    return reportBuilderState.availableMeasureIds;
  }, [reportBuilderState]);

  const availableDimensionIds = useMemo(() => {
    if (!reportBuilderState) return [];
    return reportBuilderState.availableDimensionIds;
  }, [reportBuilderState]);

  // Create combobox options for measures (sorted alphabetically)
  // For single-select charts (BAR, LINE, PIE, SINGLE_VALUE), don't disable the selected measure
  const measureOptions = useMemo<ComboboxOption[]>(() => {
    if (!measures) return [];
    const isSingleSelect = selectedChartType === 'BAR' || selectedChartType === 'LINE' || selectedChartType === 'PIE' || selectedChartType === 'SINGLE_VALUE';
    return measures
      .map(measure => ({
        value: measure.id,
        label: measure.name,
        description: measure.description,
        disabled: !availableMeasureIds.includes(measure.id) || 
                 (!isSingleSelect && selectedMeasureIds.includes(measure.id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [measures, availableMeasureIds, selectedMeasureIds, selectedChartType]);

  // Create combobox options for dimensions (sorted alphabetically)
  // For line/bar charts, exclude dimension at index 1 when used in column 0
  const dimensionOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    return dimensions
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: !availableDimensionIds.includes(dimension.id) || 
                 (selectedDimensionIds[0] !== dimension.id && selectedDimensionIds.includes(dimension.id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensions, availableDimensionIds, selectedDimensionIds]);

  // Calendar dimension options (for line chart time dimension)
  // Exclude dimension at index 1 when used in column 0
  const calendarDimensionOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions || !calendarNode) return [];
    return dimensions
      .filter(dimension => dimension.pipeline_node_id === calendarNode.id)
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: !availableDimensionIds.includes(dimension.id) || 
                 (selectedDimensionIds[0] !== dimension.id && selectedDimensionIds.includes(dimension.id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensions, calendarNode, availableDimensionIds, selectedDimensionIds]);

  // Dimension options for the second column (excluding the first dimension)
  // For line charts, also exclude calendar dimensions
  const dimensionOptions2 = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    return dimensions
      .filter(dimension => {
        // For line charts, exclude calendar dimensions
        if (selectedChartType === 'LINE' && calendarNode && dimension.pipeline_node_id === calendarNode.id) {
          return false;
        }
        return true;
      })
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: !availableDimensionIds.includes(dimension.id) || 
                 (selectedDimensionIds[1] !== dimension.id && selectedDimensionIds.includes(dimension.id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensions, availableDimensionIds, selectedDimensionIds, selectedChartType, calendarNode]);

  // Get selected measure and dimension objects for display
  const selectedMeasures = useMemo(() => {
    if (!measures) return [];
    return selectedMeasureIds
      .map(id => measures.find(m => m.id === id))
      .filter((m): m is ReportBuilderMeasure => m !== undefined);
  }, [measures, selectedMeasureIds]);

  const selectedDimensions = useMemo(() => {
    if (!dimensions) return [];
    return selectedDimensionIds
      .map(id => dimensions.find(d => d.id === id))
      .filter((d): d is ReportBuilderDimension => d !== undefined);
  }, [dimensions, selectedDimensionIds]);

  // Constraints based on visualization type
  const canAddMeasure = useMemo(() => {
    if (selectedChartType === 'TABLE') return true;
    if (selectedChartType === 'SINGLE_VALUE') return selectedMeasureIds.length === 0;
    if (selectedChartType === 'BAR' || selectedChartType === 'LINE' || selectedChartType === 'PIE') return selectedMeasureIds.length === 0;
    return false;
  }, [selectedChartType, selectedMeasureIds.length]);

  const canAddDimension = useMemo(() => {
    if (selectedChartType === 'TABLE') return true;
    if (selectedChartType === 'SINGLE_VALUE') return selectedDimensionIds.length === 0;
    if (selectedChartType === 'BAR' || selectedChartType === 'LINE') return selectedDimensionIds.length < 2;
    if (selectedChartType === 'PIE') return selectedDimensionIds.length === 0;
    return false;
  }, [selectedChartType, selectedDimensionIds.length]);

  // Handlers for adding/removing metrics and dimensions
  const handleAddMeasure = useCallback(() => {
    if (!selectedMeasureValue || !canAddMeasure) return;
    if (selectedMeasureIds.includes(selectedMeasureValue)) return;
    setSelectedMeasureIds([...selectedMeasureIds, selectedMeasureValue]);
    setSelectedMeasureValue('');
  }, [selectedMeasureValue, selectedMeasureIds, canAddMeasure]);

  const handleSetMeasure = useCallback((measureId: string) => {
    if (measureId) {
      setSelectedMeasureIds([measureId]);
    } else {
      setSelectedMeasureIds([]);
    }
  }, []);

  const handleRemoveMeasure = useCallback((measureId: string) => {
    setSelectedMeasureIds(selectedMeasureIds.filter(id => id !== measureId));
  }, [selectedMeasureIds]);

  const handleAddDimension = useCallback(() => {
    if (!selectedDimensionValue || !canAddDimension) return;
    if (selectedDimensionIds.includes(selectedDimensionValue)) return;
    setSelectedDimensionIds([...selectedDimensionIds, selectedDimensionValue]);
    setSelectedDimensionValue('');
  }, [selectedDimensionValue, selectedDimensionIds, canAddDimension]);

  const handleSetDimension0 = useCallback((dimensionId: string) => {
    const newIds: string[] = [];
    // Set dimension at index 0
    if (dimensionId) {
      newIds[0] = dimensionId;
      // Keep dimension at index 1 if it exists and is different
      if (selectedDimensionIds[1] && selectedDimensionIds[1] !== dimensionId) {
        newIds[1] = selectedDimensionIds[1];
      }
    } else {
      // If clearing, keep dimension at index 1 if it exists
      if (selectedDimensionIds[1]) {
        newIds[0] = selectedDimensionIds[1];
      }
    }
    setSelectedDimensionIds(newIds.filter(id => id));
  }, [selectedDimensionIds]);

  const handleSetDimension1 = useCallback((dimensionId: string) => {
    const newIds: string[] = [];
    // Keep dimension at index 0 if it exists
    if (selectedDimensionIds[0]) {
      newIds[0] = selectedDimensionIds[0];
    }
    // Set dimension at index 1
    if (dimensionId) {
      newIds[1] = dimensionId;
    }
    setSelectedDimensionIds(newIds.filter(id => id));
  }, [selectedDimensionIds]);

  const handleRemoveDimension = useCallback((dimensionId: string) => {
    setSelectedDimensionIds(selectedDimensionIds.filter(id => id !== dimensionId));
  }, [selectedDimensionIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Chart Configuration</DialogTitle>
          <DialogDescription>
            Select the chart type and configure your visualization settings.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0 overflow-hidden">
          <Tabs defaultValue={defaultTab} className="w-full h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-3 shrink-0">
              <TabsTrigger value="basic">Basic Settings</TabsTrigger>
              <TabsTrigger value="metrics">Metrics & Dimensions</TabsTrigger>
              <TabsTrigger value="filters">Filters</TabsTrigger>
            </TabsList>

            <div className="flex-1 min-h-0 overflow-y-auto mt-4">
              <TabsContent value="basic" className="mt-0">
                <div className="py-4">
                  <FieldGroup className="w-full">
                    <FieldSet>
                      <FieldLegend variant="label">Chart Type</FieldLegend>
                      <FieldDescription>
                        Choose the type of visualization for your chart.
                      </FieldDescription>
                      <RadioGroup
                        value={selectedChartType}
                        onValueChange={setSelectedChartType}
                        className="space-y-2"
                      >
                        {CHART_TYPES.map((type) => (
                          <FieldLabel
                            key={type.value}
                            htmlFor={`chart-type-${type.value}`}
                            className="cursor-pointer"
                          >
                            <Field 
                              orientation="horizontal"
                              className="rounded-lg border border-input p-4 transition-colors hover:bg-accent has-[[data-state='checked']]:border-primary"
                            >
                              <FieldContent>
                                <FieldTitle>{type.label}</FieldTitle>
                                <FieldDescription>{type.description}</FieldDescription>
                              </FieldContent>
                              <RadioGroupItem
                                value={type.value}
                                id={`chart-type-${type.value}`}
                              />
                            </Field>
                          </FieldLabel>
                        ))}
                      </RadioGroup>
                    </FieldSet>
                  </FieldGroup>
                </div>
              </TabsContent>

              <TabsContent value="metrics" className="mt-0">
                <div className="py-4 space-y-4">
                  <Card>
                    <CardContent className="pt-4">
                      <FieldGroup className="w-full">
                        <FieldSet>
                          <FieldLegend variant="label">Metrics</FieldLegend>
                          <FieldDescription className="mb-2">
                            {selectedChartType === 'TABLE' && 'Select one or more metrics for your table.'}
                            {selectedChartType === 'SINGLE_VALUE' && 'Select exactly one metric for your single value display.'}
                            {(selectedChartType === 'BAR' || selectedChartType === 'LINE' || selectedChartType === 'PIE') && 'Select exactly one metric for your chart.'}
                          </FieldDescription>
                          
                          {(selectedChartType === 'BAR' || selectedChartType === 'LINE' || selectedChartType === 'PIE' || selectedChartType === 'SINGLE_VALUE') ? (
                            <div className="mt-2">
                              <Combobox
                                options={measureOptions}
                                value={selectedMeasureIds[0] || ''}
                                onValueChange={handleSetMeasure}
                                placeholder="Select a metric..."
                                emptyText="No metrics available."
                              />
                            </div>
                          ) : (
                            <>
                              <div className="mt-2 flex gap-2">
                                <div className="flex-1">
                                  <Combobox
                                    options={measureOptions}
                                    value={selectedMeasureValue}
                                    onValueChange={setSelectedMeasureValue}
                                    placeholder="Select a metric..."
                                    emptyText="No metrics available."
                                    disabled={!canAddMeasure}
                                  />
                                </div>
                                <Button
                                  onClick={handleAddMeasure}
                                  disabled={!selectedMeasureValue || !canAddMeasure}
                                >
                                  Add
                                </Button>
                              </div>

                              {selectedMeasures.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <FieldDescription className="text-xs font-medium">
                                    Selected Metrics ({selectedMeasures.length})
                                  </FieldDescription>
                                  <div className="space-y-2">
                                    {selectedMeasures.map(measure => (
                                      <div
                                        key={measure.id}
                                        className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
                                      >
                                        <span>{measure.name}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={() => handleRemoveMeasure(measure.id)}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </FieldSet>
                      </FieldGroup>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-4">
                      <FieldGroup className="w-full">
                    <FieldSet>
                      <FieldLegend variant="label">Dimensions</FieldLegend>
                      <FieldDescription className="mb-2">
                        {selectedChartType === 'TABLE' && 'Select one or more dimensions for your table.'}
                        {selectedChartType === 'SINGLE_VALUE' && 'Select zero or one dimension for your single value display.'}
                        {(selectedChartType === 'BAR' || selectedChartType === 'LINE') && 'Select one or two dimensions for your chart.'}
                        {selectedChartType === 'PIE' && 'Select exactly one dimension for your pie chart.'}
                      </FieldDescription>
                      
                      {selectedChartType === 'PIE' ? (
                        <div className="mt-2">
                          <Combobox
                            options={dimensionOptions}
                            value={selectedDimensionIds[0] || ''}
                            onValueChange={handleSetDimension0}
                            placeholder="Select a dimension..."
                            emptyText="No dimensions available."
                          />
                        </div>
                      ) : (selectedChartType === 'LINE' || selectedChartType === 'BAR') ? (
                        <div className="mt-2 grid grid-cols-2 gap-4">
                          {/* Left column - Time dimension for line charts, any dimension for bar charts */}
                          <div className="space-y-1">
                            <FieldLabel className="text-sm font-medium">
                              {selectedChartType === 'LINE' ? 'Time Dimension' : 'One bar per'}
                            </FieldLabel>
                            <Combobox
                              options={selectedChartType === 'LINE' ? calendarDimensionOptions : dimensionOptions}
                              value={selectedDimensionIds[0] || ''}
                              onValueChange={handleSetDimension0}
                              placeholder={selectedChartType === 'LINE' ? 'Select time dimension...' : 'Select a dimension...'}
                              emptyText="No dimensions available."
                            />
                          </div>

                          {/* Right column - Optional "One line per" dimension */}
                          <div className="space-y-1">
                            <FieldLabel className="text-sm font-medium">
                              {selectedChartType === 'LINE' ? 'One line per' : 'Color bars by'}
                            </FieldLabel>
                            <Combobox
                              options={dimensionOptions2}
                              value={selectedDimensionIds[1] || ''}
                              onValueChange={handleSetDimension1}
                              placeholder="Optional..."
                              emptyText="No dimensions available."
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mt-2 flex gap-2">
                            <div className="flex-1">
                              <Combobox
                                options={dimensionOptions}
                                value={selectedDimensionValue}
                                onValueChange={setSelectedDimensionValue}
                                placeholder="Select a dimension..."
                                emptyText="No dimensions available."
                                disabled={!canAddDimension}
                              />
                            </div>
                            <Button
                              onClick={handleAddDimension}
                              disabled={!selectedDimensionValue || !canAddDimension}
                            >
                              Add
                            </Button>
                          </div>

                          {selectedDimensions.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <FieldDescription className="text-xs font-medium">
                                Selected Dimensions ({selectedDimensions.length})
                              </FieldDescription>
                              <div className="space-y-2">
                                {selectedDimensions.map(dimension => (
                                  <div
                                    key={dimension.id}
                                    className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  >
                                    <span>{dimension.name}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => handleRemoveDimension(dimension.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </FieldSet>
                  </FieldGroup>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="filters" className="mt-0">
                <div className="py-4">
                  <FieldGroup className="w-full">
                    <FieldSet>
                      <FieldLegend variant="label">Filters</FieldLegend>
                      <FieldDescription>
                        Add filters to refine your chart data.
                      </FieldDescription>
                      <div className="mt-4">
                        <FilterTable
                          filters={dimensionFilters.map(f => ({
                            columnId: f.dimension_id,
                            comparator: f.comparator,
                            value: f.value,
                          }))}
                          onChange={(newFilters) => {
                            setDimensionFilters(newFilters.map(f => ({
                              dimension_id: f.columnId,
                              comparator: f.comparator,
                              value: f.value,
                            })));
                          }}
                          columnOptions={dimensionOptions}
                          columnLabel="Dimension"
                          showComparator={true}
                          comparatorOptions={defaultComparatorOptions}
                          defaultComparator="IN"
                          defaultValue={[]}
                          valueMode="multiselect"
                          onFetchValueOptions={fetchDimensionValues}
                        />
                      </div>
                    </FieldSet>
                  </FieldGroup>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </DialogBody>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApplyChanges} disabled={!selectedChartType}>
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

